import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  User,
  Mail,
  Shield,
  Key,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Smartphone
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import ChangePasswordModal from './ChangePasswordModal';

const API = process.env.REACT_APP_BACKEND_URL;

const MyProfilePage = () => {
  const { user, isAdmin } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pinState, setPinState] = useState({
    loading: false,
    tem_pin: false,
    pin: null,
    error: null,
  });

  useEffect(() => {
    const fetchMyPin = async () => {
      if (!user?.employee_id) {
        setPinState({ loading: false, tem_pin: false, pin: null, error: 'no_employee' });
        return;
      }

      try {
        setPinState(prev => ({ ...prev, loading: true, error: null }));
        const response = await axios.get(`${API}/api/ponto-app/meu-pin`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPinState({
          loading: false,
          tem_pin: !!response.data?.tem_pin,
          pin: response.data?.pin || null,
          error: null,
        });
      } catch (error) {
        setPinState({
          loading: false,
          tem_pin: false,
          pin: null,
          error: error?.response?.data?.detail || 'load_failed',
        });
      }
    };

    fetchMyPin();
  }, [user?.employee_id]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Meu Perfil</h1>
        <p className="text-slate-500 mt-1">Informações da sua conta</p>
      </div>

      {/* User Info Card */}
      <Card className="shadow-md border-0">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 rounded-t-xl border-b border-slate-100">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center shadow-lg">
              <User className="h-8 w-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl text-slate-800">{user?.name}</CardTitle>
              <CardDescription className="text-slate-500">
                {isAdmin ? 'Administrador' : 'Usuário'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
            <Mail className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500 font-medium">E-mail</p>
              <p className="text-sm text-slate-700">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
            <Shield className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500 font-medium">Tipo de Acesso</p>
              <p className="text-sm text-slate-700">{isAdmin ? 'Administrador' : 'Usuário'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800 flex items-center">
            <Smartphone className="h-5 w-5 mr-2 text-emerald-500" />
            Meu PIN do Ponto-App
          </CardTitle>
          <CardDescription className="text-slate-500">
            Visualize apenas o PIN vinculado ao seu colaborador
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pinState.loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : pinState.error === 'no_employee' ? (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
              Seu usuário não está vinculado a um colaborador.
            </div>
          ) : pinState.tem_pin ? (
            pinState.pin ? (
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-700 font-medium mb-2">PIN ativo</p>
                <div className="text-3xl font-mono font-bold tracking-widest text-emerald-800">
                  {pinState.pin}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                Seu PIN é legado e precisa ser regenerado pelo RH para ficar visível no Grid.
              </div>
            )
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
              Nenhum PIN foi gerado para seu acesso ao Ponto-App.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800 flex items-center">
            <Key className="h-5 w-5 mr-2 text-blue-500" />
            Segurança
          </CardTitle>
          <CardDescription className="text-slate-500">
            Gerencie sua senha de acesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.password_must_change && (
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <AlertTriangle className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-blue-600 font-medium">Primeiro acesso</p>
                <p className="text-sm text-blue-700">Você precisa redefinir sua senha.</p>
              </div>
            </div>
          )}
          {user?.force_password_reset && (
            <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-xs text-orange-600 font-medium">Redefinir senha no próximo login</p>
                <p className="text-sm text-orange-700">
                  Administrador solicitou redefinição de senha. Faça login novamente.
                </p>
              </div>
            </div>
          )}
          {!user?.password_must_change && !user?.force_password_reset && (
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <RefreshCw className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-xs text-green-600 font-medium">Status da senha</p>
                <p className="text-sm text-green-700">Senha OK</p>
              </div>
            </div>
          )}

          <Button
            onClick={() => setShowPasswordModal(true)}
            className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white"
          >
            <Key className="h-4 w-4 mr-2" />
            Alterar Senha
          </Button>
          <p className="text-xs text-slate-400">
            Sua senha deve ter no mínimo 6 caracteres
          </p>
        </CardContent>
      </Card>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        isExpired={false}
      />
    </div>
  );
};

export default MyProfilePage;
